import moment, { Moment } from 'moment';
import { v4 as uuidv4 } from 'uuid';
import {
	type CreateSpeedFactor,
	type SpeedFactor
} from '@bringg/dashboard-sdk/dist/SpeedFactor/Service/SpeedFactor.service';

import { getDayAndMonthNumbers, getShiftedDayOfTheWeek, isRangeIntersectsUTCMidnight } from './date';
import { formatFactorForDb, getIntervals } from './generic';
import { type RangeProcessingData, type RangeRecord, ShiftDirection } from './types';
import { removeInvalidWeekDays } from './week-day';

type DayMonthSplitProps = {
	applicableDatesStart: Moment;
	applicableDatesEnd: Moment;
	shiftDirection: ShiftDirection;
};

type DayMonthSplitItem = {
	start_day?: number;
	start_month?: number;
	end_day?: number;
	end_month?: number;
};

const getDayAndMonthSplit = ({
	applicableDatesStart,
	applicableDatesEnd,
	shiftDirection
}: DayMonthSplitProps): [DayMonthSplitItem, DayMonthSplitItem] => {
	const dayMonthArrayToHash = ([start_day, start_month, end_day, end_month]: number[]): DayMonthSplitItem => ({
		start_day,
		start_month,
		end_day,
		end_month
	});

	// Important! Dates here must be in UTC, as we interested specifically in UTC day, month here
	const dayMonth = dayMonthArrayToHash(getDayAndMonthNumbers([applicableDatesStart, applicableDatesEnd]));

	const getDayMonthSplitUp = (): DayMonthSplitItem => {
		const isStartEndOfYear = dayMonth.start_month === 12 && dayMonth.start_day === 31;
		const isEndEndOfYear = dayMonth.end_month === 12 && dayMonth.end_day === 31;

		return dayMonthArrayToHash(
			getDayAndMonthNumbers([
				isStartEndOfYear ? applicableDatesStart : moment(applicableDatesStart).add(1, 'day'),
				isEndEndOfYear ? applicableDatesEnd : moment(applicableDatesEnd).add(1, 'day')
			])
		);
	};

	const getDayMonthSplitDown = (): DayMonthSplitItem => {
		const isStartStartOfYear = dayMonth.start_month === 1 && dayMonth.start_day === 1;
		const isEndStartOfYear = dayMonth.end_month === 1 && dayMonth.end_day === 1;

		return dayMonthArrayToHash(
			getDayAndMonthNumbers([
				isStartStartOfYear ? applicableDatesStart : moment(applicableDatesStart).subtract(1, 'day'),
				isEndStartOfYear ? applicableDatesEnd : moment(applicableDatesEnd).subtract(1, 'day')
			])
		);
	};

	// We can't resize / dnd calendar entities beyond the year borders
	// it will make applicable dates range broken because back-end doesn't store the year anyhow
	const getDayMonthSplit = (): [DayMonthSplitItem, DayMonthSplitItem] => {
		switch (shiftDirection) {
			case ShiftDirection.Up:
				return [dayMonth, getDayMonthSplitUp()];
			case ShiftDirection.Down:
				return [getDayMonthSplitDown(), dayMonth];
			default:
				return [dayMonth, Object.assign({}, dayMonth)];
		}
	};

	return getDayMonthSplit();
};

type SplitRangeProps = {
	daysOfWeek: number[];
	intervalStart: number;
	intervalEnd: number;
	adjustEffectiveDates?: boolean;
} & DayMonthSplitProps;

type SplitRangeItem = DayMonthSplitItem & {
	daysOfWeek: number[];
	intervals: [number, number][];
};

const splitRangeData = ({
	daysOfWeek,
	intervalStart,
	intervalEnd,
	shiftDirection,
	applicableDatesStart,
	applicableDatesEnd,
	adjustEffectiveDates
}: SplitRangeProps): SplitRangeItem[] => {
	if (shiftDirection === ShiftDirection.None) {
		return [{ daysOfWeek: daysOfWeek, intervals: [[intervalStart, intervalEnd]] }];
	}

	const splitIntervals: [number, number][] = [
		[intervalStart, 24],
		[0, intervalEnd]
	];

	const shiftedDaysOfWeek = daysOfWeek.map(d => getShiftedDayOfTheWeek(d, shiftDirection));
	const splitDaysOfWeek =
		shiftDirection === ShiftDirection.Up ? [daysOfWeek, shiftedDaysOfWeek] : [shiftedDaysOfWeek, daysOfWeek];

	const applicableDatesSplit = getDayAndMonthSplit({
		applicableDatesStart,
		applicableDatesEnd,
		// So far, we want to adjust effective dates only for "current" part of the range of the "edit" operation
		shiftDirection: adjustEffectiveDates ? shiftDirection : ShiftDirection.None
	});

	return [
		{ daysOfWeek: splitDaysOfWeek[0], intervals: [splitIntervals[0]], ...applicableDatesSplit[0] },
		{ daysOfWeek: splitDaysOfWeek[1], intervals: [splitIntervals[1]], ...applicableDatesSplit[1] }
	];
};

export const splitRange = ({
	daysOfWeek,
	intervalStart,
	intervalEnd,
	applicableDatesStart,
	applicableDatesEnd,
	shiftDirection,
	adjustEffectiveDates,
	factor,
	service_area_id
}: SplitRangeProps & { factor: number; service_area_id: number }) => {
	const splitData = splitRangeData({
		daysOfWeek,
		intervalStart,
		intervalEnd,
		applicableDatesStart,
		applicableDatesEnd,
		shiftDirection,
		adjustEffectiveDates
	});
	const original_uuid = uuidv4();

	return splitData.map(({ daysOfWeek, intervals, start_day, start_month, end_day, end_month }) => ({
		intervals,
		original_uuid,
		service_area_id,
		factor,
		start_date_day: start_day,
		start_date_month: start_month,
		end_date_day: end_day,
		end_date_month: end_month,
		days_of_week: daysOfWeek
	}));
};

type PrepareRangesProps =
	| {
			operationType: 'update';
			ranges: RangeRecord[];
			speedFactorData: Pick<SpeedFactor, 'factor' | 'service_area_id' | 'interval_start' | 'interval_end'>;
			timezone: string;
	  }
	| {
			operationType: 'create';
			ranges: RangeRecord[];
			speedFactorData: Pick<SpeedFactor, 'service_area_id'>;
			timezone: string;
	  }
	| {
			operationType: 'split-on-delete';
			ranges: RangeRecord[];
			speedFactorData: Pick<SpeedFactor, 'factor' | 'service_area_id' | 'interval_start' | 'interval_end'>;
			timezone: string;
	  };

export const prepareRanges = (props: PrepareRangesProps): CreateSpeedFactor[] => {
	const { operationType, ranges, speedFactorData } = props;

	const getRangeProcessingData = ({ effectiveStartDate, effectiveEndDate }: RangeRecord): RangeProcessingData => ({
		effectiveStartDate,
		effectiveEndDate
	});

	return ranges
		.reduce<{ speedFactorConfig: CreateSpeedFactor; processingData: RangeProcessingData }[]>((acc, range) => {
			const { effectiveStartDate, effectiveEndDate, daysOfWeek, formData, adjustEffectiveDates } = range;
			const processingData = getRangeProcessingData(range);

			const factor =
				formData || operationType === 'create' ? formatFactorForDb(formData.factor) : speedFactorData.factor;

			const intervals: [number, number][] =
				formData || operationType === 'create'
					? getIntervals(formData)
					: [[speedFactorData.interval_start, speedFactorData.interval_end]];

			let hasToSplitRange = false;
			const [hoursIntervalStart, hoursIntervalEnd] = intervals[0];

			// hoursIntervalEnd === hoursIntervalStart is moving fullDay range / creating fullDay range in non-UTC
			if (hoursIntervalStart > hoursIntervalEnd || hoursIntervalEnd === hoursIntervalStart) {
				if (formData) {
					const [calendarIntervalStart, calendarIntervalEnd] = formData.intervals;

					if (isRangeIntersectsUTCMidnight(calendarIntervalStart, calendarIntervalEnd)) {
						hasToSplitRange = true;
					} else {
						console.error('Traffic Variables. False positive multi day check');
					}
				} else {
					// if !formData original speedFactor interval is used
					hasToSplitRange = true;
					console.warn(`Traffic Variables. Midnight split is handled without formData`);
				}
			}

			if (hasToSplitRange) {
				acc.push(
					...splitRange({
						factor,
						daysOfWeek,
						adjustEffectiveDates,
						// Constant ShiftDirection.Up should work fine until we use corrected days_of_week
						// checkout the getCorrectedISOWeekDays function
						shiftDirection: ShiftDirection.Up,
						intervalStart: hoursIntervalStart,
						intervalEnd: hoursIntervalEnd,
						applicableDatesStart: effectiveStartDate,
						applicableDatesEnd: effectiveEndDate,
						service_area_id: speedFactorData.service_area_id
					}).map(splitPiece => ({
						processingData,
						speedFactorConfig: splitPiece
					}))
				);
			} else {
				// startDate / endDate must be in UTC as function works in timezone agnostic way
				// and the data prepared here for the back-end here must be stored in UTC
				const [start_day, start_month, end_day, end_month] = getDayAndMonthNumbers([
					effectiveStartDate,
					effectiveEndDate
				]);

				acc.push({
					processingData,
					speedFactorConfig: {
						service_area_id: speedFactorData.service_area_id,
						factor: factor,
						start_date_day: start_day,
						start_date_month: start_month,
						end_date_day: end_day,
						end_date_month: end_month,
						days_of_week: daysOfWeek,
						intervals,
						original_uuid: uuidv4()
					}
				});
			}

			return acc;
		}, [])
		.map(removeInvalidWeekDays)
		.filter(range => range.days_of_week.length);
};
